import * as filtersHelper from "@/helpers/filters.helper"
import { Filters } from "@/organisms/SmartSearch/types/options.types"
import {
  getRuntimeFilter,
  getSeverityFilter,
  InventoryFilters
} from "@/pages/DiscoveryPage/utils/filters.utils"

import type { TFunction } from "@/hooks/useTranslation"

import { WorkLoadStatuses } from "@/domain/application"

export const getWorkloadTypeOptions = () => [
  { label: "ReplicaSet", value: "replicaSet" },
  { label: "Deployment", value: "deployment" },
  { label: "StatefulSet", value: "statefulSet" },
  { label: "DaemonSet", value: "daemonSet" },
  { label: "Job", value: "job" },
  { label: "CronJob", value: "cronJob" },
  { label: "Custom", value: "custom" },
  { label: "BarePod", value: "barePod" },
  { label: "ArgoRollout", value: "argoRollout" },
  { label: "KedaScaledJob", value: "kedaScaledJob" },
  { label: "ECSReplica", value: "ecsReplica" },
  { label: "ECSDaemon", value: "ecsDaemon" },
  { label: "AWS Lambda", value: "lambdaFunction" },
  { label: "Azure App Service", value: "azureAppService" },
  { label: "Azure Functions", value: "azureFunctions" },
  { label: "Azure Container App", value: "azureContainerApp" },
  { label: "AgentCore Runtime", value: "agentcoreRuntime" },
  { label: "Foundry Agent", value: "foundryAgent" },
  { label: "VM Container", value: "vmContainer" }
]

export const getWorkloadStatusOptions = (t: TFunction) =>
  WorkLoadStatuses.map((value) => ({
    label: t(`inventory.application.statuses.${value}`),
    value
  }))

export const getDefaultFilters = (
  t: TFunction,
  environmentOptions?: { label: string; value: string }[]
): Filters => [
  InventoryFilters.application(t),
  InventoryFilters.repositoryName(t),
  InventoryFilters.imageName(t),
  filtersHelper.getBaseImageNameFilter(t),
  filtersHelper.getBaseImageDigestFilter(t),
  InventoryFilters.environment(t, environmentOptions),
  InventoryFilters.namespace(t),
  getRuntimeFilter(),
  getSeverityFilter(),
  filtersHelper.getVulnerabilityIdFilter(t),
  filtersHelper.getCveIdFilter(t),
  filtersHelper.getHighProfileFilter(t),
  {
    ...filtersHelper.getOsFilter(t),
    // We override key here to match the key from BE
    key: "operatingSystem"
  },
  {
    key: "ingress",
    order: 3,
    label: t("filters.ingress"),
    type: "single",
    options: [
      { label: "Yes", value: "YES", disabled: false },
      { label: "No", value: "NO", disabled: false }
    ]
  },
  {
    key: "publicImage",
    order: 2,
    label: t("filters.publicImage"),
    type: "single",
    options: [
      { label: "Yes", value: "YES", disabled: false },
      { label: "No", value: "NO", disabled: false }
    ]
  },
  filtersHelper.getPackageCategoryAI(t),
  filtersHelper.getIsPackageAiRelated(t, filtersHelper.getYesNoOptions),
  {
    key: "workloadType",
    order: 2,
    label: t("filters.workloadType"),
    type: "multiple",
    options: getWorkloadTypeOptions()
  },
  {
    key: "workloadStatus",
    order: 2,
    label: t("filters.workloadStatus"),
    type: "multiple",
    options: getWorkloadStatusOptions(t)
  },
  {
    key: "projectName",
    label: t("filters.projectName"),
    type: "input"
  }
]

export const getFilters = (
  t: TFunction,
  props?: {
    internetFacingFF?: boolean
    environmentOptions?: { label: string; value: string }[]
  }
) => {
  if (props?.internetFacingFF) {
    const filters = getDefaultFilters(t, props?.environmentOptions).map(
      (filter) => {
        if (filter.key === "workloadStatus" || filter.key === "workloadType")
          return filter

        if (filter.options) {
          filter.options.push({ label: "N/A", value: "", disabled: true })
        }

        return filter
      }
    )

    return [
      {
        key: "internetFacing",
        label: t("filters.internetFacing"),
        type: "single",
        order: 1,
        options: [
          { label: "Yes", value: "YES", disabled: false },
          { label: "No", value: "NO", disabled: false },
          {
            displayLabel: "N/A",
            label: "Undetermined",
            value: "UNDETERMINED",
            disabled: false
          }
        ]
      },
      ...filters
    ]
  }

  return getDefaultFilters(t, props?.environmentOptions)
}
